/**
 * Install-prompt plumbing for the PWA.
 *
 * `beforeinstallprompt` is fired once, early, and only Chromium browsers fire it
 * at all. Listening from inside a component is a race: App.vue hides the whole
 * shell behind a screen-width gate, so on a narrow viewport the banner may not
 * be mounted yet when the event arrives — and the browser never re-fires it.
 * So the event is captured here, at module scope, from `main.ts` before the app
 * mounts, and parked in reactive state the banner reads whenever it does mount.
 */
import { reactive } from 'vue';

/** The Chromium-only event that lets a page defer the native install prompt. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export const installState = reactive({
  /** A native install prompt has been captured and is ready to show. */
  canInstall: false,
  /** The app is installed — either already running standalone, or just added. */
  installed: false,
});

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let started = false;

/** True when the app is running from the home screen rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia
    && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari exposes navigator.standalone instead of display-mode.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(displayMode || iosStandalone === true);
}

function isIos(): boolean {
  const nav = window.navigator;
  const ua = nav.userAgent || '';
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as MacIntel but has touch points.
  const iPadOS = nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

/**
 * True on the only browser that can "Add to Home Screen" on iOS. Chrome,
 * Firefox, Edge and Opera for iOS cannot, so they must not be told how to.
 */
export function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return isIos() && /safari/i.test(ua) && !/crios|fxios|edgios|opios/i.test(ua);
}

function onBeforeInstallPrompt(event: Event): void {
  // Stop Chrome's default mini-infobar; the app shows its own banner instead.
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  installState.canInstall = true;
}

function onAppInstalled(): void {
  deferredPrompt = null;
  installState.canInstall = false;
  installState.installed = true;
}

/** Starts listening. Safe to call more than once; only the first call binds. */
export function startInstallPromptCapture(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  installState.installed = isStandalone();

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

/**
 * Shows the browser's native install dialog and resolves with what the user
 * chose. A captured prompt can only be used once, so it is released either way.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const promptEvent = deferredPrompt;
  if (!promptEvent) return 'unavailable';

  deferredPrompt = null;
  installState.canInstall = false;

  try {
    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    return outcome;
  } catch (err) {
    return 'unavailable';
  }
}
